import type { SalesLookupClosedRow, SalesLookupVariant } from 'apis/sales-lookup';

// Variant narrowing for the detail screen: one chip row per facet (brand,
// origin, state, packaging UOM, plus every attribute type the family carries),
// then (if still ambiguous) an explicit variant picker. `null` is a real
// selectable value ("No brand" / "Unknown"), distinct from an absent selection
// key (facet not filtering — "All").

export const activeVariants = (variants: SalesLookupVariant[]): SalesLookupVariant[] =>
  variants.filter((v) => v.isActive);

const uniqueInOrder = (values: (string | null)[]): (string | null)[] => {
  const seen = new Set<string | null>();
  const result: (string | null)[] = [];
  for (const value of values) {
    if (!seen.has(value)) {
      seen.add(value);
      result.push(value);
    }
  }
  return result;
};

export interface FacetDef {
  key: string; // 'brand' | 'origin' | 'state' | 'packaging' | 'attr:<attributeType>'
  label: string;
  nullLabel: string;
}

// undefined (or missing key) = facet not filtering ("All"); null = the
// explicit "none" value of that facet.
export type FacetSelection = Record<string, string | null | undefined>;

const ATTR_PREFIX = 'attr:';

// The outermost unit word — what "packaging UOM" means to a salesperson
// (carton, tub, pc, kg).
export const packagingWord = (v: SalesLookupVariant): string => v.packUnit ?? v.itemUnit ?? v.baseUnit;

export function facetValue(v: SalesLookupVariant, key: string): string | null {
  switch (key) {
    case 'brand':
      return v.brand;
    case 'origin':
      return v.origin;
    case 'state':
      return v.state;
    case 'packaging':
      return packagingWord(v);
    default: {
      const type = key.slice(ATTR_PREFIX.length);
      return v.attributes.find((a) => a.attributeType === type)?.attributeValue ?? null;
    }
  }
}

// Base facets in display order, then one facet per attribute type present on
// any active variant (encounter order).
export function facetDefs(variants: SalesLookupVariant[]): FacetDef[] {
  const defs: FacetDef[] = [
    { key: 'brand', label: 'BRAND', nullLabel: 'No brand' },
    { key: 'origin', label: 'ORIGIN', nullLabel: 'Unknown' },
    { key: 'state', label: 'STATE', nullLabel: 'Unspecified' },
    { key: 'packaging', label: 'PACKAGING', nullLabel: 'Unspecified' },
  ];

  const seen = new Set<string>();
  for (const v of activeVariants(variants)) {
    for (const a of v.attributes) {
      if (seen.has(a.attributeType)) continue;
      seen.add(a.attributeType);
      defs.push({ key: `${ATTR_PREFIX}${a.attributeType}`, label: a.attributeType.toUpperCase(), nullLabel: 'Unspecified' });
    }
  }
  return defs;
}

// Every value the facet takes across active variants — deliberately NOT
// narrowed by the current selection, so any value is always one tap away
// (applyFacet clears whatever the tap contradicts).
export function facetOptions(variants: SalesLookupVariant[], key: string): (string | null)[] {
  return uniqueInOrder(activeVariants(variants).map((v) => facetValue(v, key)));
}

export function filterVariants(variants: SalesLookupVariant[], selection: FacetSelection): SalesLookupVariant[] {
  const entries = Object.entries(selection).filter(([, value]) => value !== undefined);
  return activeVariants(variants).filter((v) => entries.every(([key, value]) => facetValue(v, key) === value));
}

// Apply one facet pick. Selections on other facets that would empty the match
// set are cleared (in facet order) — a tap always lands on a non-empty result,
// mirroring the old "changing brand resets origin" cascade.
export function applyFacet(
  variants: SalesLookupVariant[],
  selection: FacetSelection,
  key: string,
  value: string | null | undefined
): FacetSelection {
  let next: FacetSelection = { ...selection, [key]: value };
  if (value === undefined) return next;

  for (const def of facetDefs(variants)) {
    if (def.key === key || next[def.key] === undefined) continue;
    if (filterVariants(variants, next).length) break;
    next = { ...next, [def.key]: undefined };
  }
  if (!filterVariants(variants, next).length) next = { [key]: value };
  return next;
}

// Default selection: the active variant with the most recent closed price —
// the one the salesperson most likely gets asked about — else the first
// active variant, else null.
export function defaultVariant(
  variants: SalesLookupVariant[],
  closed: SalesLookupClosedRow[]
): SalesLookupVariant | null {
  const active = activeVariants(variants);
  if (!active.length) return null;

  const activeIds = new Set(active.map((v) => v.id));
  let latest: SalesLookupClosedRow | null = null;
  for (const row of closed) {
    if (!activeIds.has(row.variantId)) continue;
    if (!latest || new Date(row.closedAt).getTime() > new Date(latest.closedAt).getTime()) latest = row;
  }

  const hit = latest ? active.find((v) => v.id === latest?.variantId) : undefined;
  return hit ?? active[0];
}

// Which variant a price log targets. An explicit variantId that no longer
// resolves returns null — silently falling back to another variant would log
// the price against a product the salesperson never looked at. An inactive
// target (e.g. a stale bookmark) gets the same not-found treatment as a
// missing one.
export function resolveLoggedVariant(
  variants: SalesLookupVariant[],
  closed: SalesLookupClosedRow[],
  variantId?: number
): SalesLookupVariant | null {
  if (variantId != null) return variants.find((v) => v.id === variantId && v.isActive) ?? null;
  return defaultVariant(variants, closed);
}

// The pack format as a salesperson says it: "carton of 10 × 1.1 kg", else the
// outermost unit word.
export function packagingLabel(variant: SalesLookupVariant): string {
  return variant.packUnit && variant.packSize && variant.netContent
    ? `${variant.packUnit} of ${variant.packSize} × ${variant.netContent} ${variant.baseUnit}`
    : variant.packUnit || variant.itemUnit || variant.baseUnit;
}

// Compact one-line label for the variant picker row.
export function variantLabel(variant: SalesLookupVariant): string {
  return [variant.brand ?? 'No brand', variant.origin, variant.state, variant.spec, packagingLabel(variant)]
    .filter(Boolean)
    .join(' · ');
}
